// Globale Quelle für die Sidebar-Badges (#48). Die Zähler werden EINMAL zentral
// ermittelt und über Context bereitgestellt, damit sie auf jeder Seite identisch
// sind — vorher kamen sie aus Seiten-Props und verschwanden je nach aktiver Route.
//
// AppShell konsumiert nur den Context (reines useContext, kein React-Query) → die
// vielen Seiten-Tests, die AppShell ohne QueryClientProvider rendern, bleiben
// unberührt. Die eigentlichen Abfragen laufen im Provider, der einmalig um die
// authentifizierte App liegt; React-Query dedupliziert sie mit den Seiten-Hooks.
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useAgents, useCases, useAgentRequests, useVmRequests } from '../api/hooks'
import { useMeCtx } from '../auth/MeContext'
import { can } from '../rbac/roles'
import { displayStatus } from '../lib/liveStatus'
import { isOpenStatus } from '../lib/requestStatus'
import { caseStatusKey } from '../lib/enums'

export interface NavCounts {
  offline?: number
  openCases?: number
  openRequests?: number
  // #137: true, wenn mind. ein offener Vorgang AKTIV alarmiert (nicht resolved/extern) —
  // steuert den Alarm-Ton des Vorgänge-Badges. false = alle offenen warten nur aufs Schließen.
  casesAlarming?: boolean
}

export const NavCountsContext = createContext<NavCounts>({})
export const useNavCounts = () => useContext(NavCountsContext)

export function NavCountsProvider({ children }: { children: ReactNode }) {
  const { role } = useMeCtx()
  const canManageRequests = can(role, 'manageRequests')

  const { data: agents } = useAgents(true)
  const { data: cases } = useCases(true)
  const { data: agentReqs } = useAgentRequests(undefined, canManageRequests)
  const { data: vmReqs } = useVmRequests(undefined, canManageRequests)

  const value = useMemo<NavCounts>(
    () => ({
      offline: agents ? agents.filter((a) => displayStatus(a) === 'offline').length : undefined,
      openCases: cases ? cases.length : undefined,
      casesAlarming: cases
        ? cases.some((c) => {
            const k = caseStatusKey(c.status)
            return k !== 'resolved' && k !== 'extern' && k !== 'closed'
          })
        : undefined,
      openRequests: canManageRequests
        ? [...(agentReqs ?? []), ...(vmReqs ?? [])].filter((r) => isOpenStatus(r.status)).length
        : undefined,
    }),
    [agents, cases, agentReqs, vmReqs, canManageRequests],
  )

  return <NavCountsContext.Provider value={value}>{children}</NavCountsContext.Provider>
}
